import { IP_INFO_TIMEOUT_MS, IP_INFO_URL, displayable } from './config';
import { timeoutSignal } from './timeout';

/**
 * Public-IP / geo / carrier snapshot from `ipwho.is` (SPEC §8).
 *
 * The one part of the device self-report that is **not** weaker than Android's:
 * Android reads the same service for the same reason. A no-signal SIM keeps
 * stale operator names that flip between carriers, so the SIM is the wrong
 * source even where you can read it.
 *
 * **Carries no device key.** It is a third-party service — same invariant as the
 * probe, and the same reason the Android app keeps two HTTP clients apart.
 *
 * Total / best-effort: any failure (network, non-2xx, bad JSON, `success:false`)
 * yields `null` and the caller keeps whatever it had. A missing region label is
 * a shrug; a cycle lost to a geo lookup would not be.
 */
export interface IpInfo {
  ip: string | null;
  isp: string | null;
  region: string | null;
  city: string | null;
  country: string | null;
  countryCode: string | null;
  flagEmoji: string | null;
  /** The whole object, forwarded to the backend verbatim. */
  raw: Record<string, unknown>;
}

/**
 * Session cache. Lost when the tab closes, which is acceptable precisely because
 * the app does not survive the tab closing either (SPEC §9).
 */
let cached: IpInfo | null = null;

export async function fetchIpInfo(
  fetchImpl: typeof fetch = globalThis.fetch,
  endpoint: string = IP_INFO_URL,
): Promise<IpInfo | null> {
  const { signal, cancel } = timeoutSignal(IP_INFO_TIMEOUT_MS);
  try {
    const response = await fetchImpl(endpoint, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      signal,
    });
    if (!response.ok) return null;
    const raw = (await response.json()) as Record<string, unknown>;
    if (raw.success !== true) return null;
    return parse(raw);
  } catch {
    return null;
  } finally {
    cancel();
  }
}

/** The cached snapshot, fetching once per session if needed. */
export async function ipInfo(fetchImpl?: typeof fetch): Promise<IpInfo | null> {
  if (cached) return cached;
  cached = await fetchIpInfo(fetchImpl);
  return cached;
}

/** Test seam. */
export function resetIpInfoCache(): void {
  cached = null;
}

export function parse(raw: Record<string, unknown>): IpInfo {
  const connection = obj(raw.connection);
  const flag = obj(raw.flag);
  return {
    ip: str(raw.ip),
    // `isp` is the operator name ("nhà mạng"); `org` is the usual fallback when
    // the ISP field is blank for a mobile ASN.
    isp: str(connection?.isp) ?? str(connection?.org),
    region: str(raw.region),
    city: str(raw.city),
    country: str(raw.country),
    countryCode: str(raw.country_code),
    flagEmoji: str(flag?.emoji),
    raw,
  };
}

/** "Ho Chi Minh City, Vietnam" — de-duped, blanks dropped. */
export function displayRegion(info: IpInfo | null): string | null {
  if (!info) return null;
  const parts = [info.city, info.region, info.country]
    .map((p) => p?.trim())
    .filter((p): p is string => !!p && p !== '');
  const unique = [...new Set(parts)];
  return unique.length === 0 ? null : unique.join(', ');
}

/**
 * The public IP out of the **raw** ipwho.is record.
 *
 * Takes the raw object rather than a parsed `IpInfo` because the raw record is
 * what a cycle carries in its `MeasurementContext` and forwards to the backend
 * verbatim — the screens that show an IP have that, not an `IpInfo`.
 *
 * Everything about reading it is delegated: `str` because some ipwho.is fields
 * arrive numeric and a bare `String(value)` on an absent one would print the
 * word "undefined" as an IP address, and `displayable` because the `UNKNOWN`
 * wire sentinel must never reach a screen (SPEC §8). This used to be written out
 * twice — once in the connection module and once in Settings — and the two
 * copies had already drifted: only one of them filtered the sentinel.
 */
export function ipOf(raw: Record<string, unknown> | null | undefined): string | null {
  return displayable(str(raw?.ip));
}

function obj(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

/** Trimmed non-blank string, else null. Survives numbers (some fields arrive numeric). */
function str(value: unknown): string | null {
  if (typeof value === 'number') return String(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
